import React, { useEffect, useState } from 'react';
import { useTheme, Theme } from '@mui/material/styles';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import { StringParam, useQueryParam, useQueryParams } from 'use-query-params';
import { CircularProgress, Box, Backdrop, Modal, Typography, Button, TextField } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { ELMSType, IMentorkitLMSLinkRequest } from '../../services/models/LMSLinkModel';
import lmsIntegrationAgent from '../../services/api/lmsIntegrationAgent';
import SnackbarUtils from '../../components/snackbarUtils/SnackbarUtilsConfigurator';

interface IntegrationComponentProps {
  ownerID: number;
  loadingOwnerID: boolean;
}

enum EIntegrationStatus {
  notStarted,
  pending,
  completed,
  error,
}

//TODO: Legg til input feltet igjen.

export default function NewMentorkitLMSIntegration({ ownerID, loadingOwnerID }: IntegrationComponentProps) {
  const theme = useTheme();
  const history = useHistory();
  const [apiKeyParam, setApiKeyParam] = useQueryParam('apiKey', StringParam);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [apiKey, setApiKey] = useState('');
  //const [showProcessingModal, setShowProcessingModal] = useState(false);

  const [integrationStatus, setIntegrationStatus] = useState<EIntegrationStatus>(EIntegrationStatus.notStarted);
  const [hasRun, setHasRun] = useState(false);

  const handleApiKeyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setApiKeyInput(event.target.value);
  };

  const handleSubmitApiKey = () => {
    setApiKey(apiKeyInput);
    setIntegrationStatus(EIntegrationStatus.pending);
  };

  //reset query params on unmount
  useEffect(() => {
    return () => {
      setApiKeyParam(undefined);
    };
  }, []);

  useEffect(() => {
    if (apiKeyParam && apiKeyParam !== '') {
      setApiKey(apiKeyParam);
      setIntegrationStatus(EIntegrationStatus.pending);
    }
  }, [apiKeyParam]);

  // Handle submission when we have both owner ID and URL params
  useEffect(() => {
    if (apiKey !== '' && ownerID && !loadingOwnerID) {
      handleNewIntegration();
    }
  }, [apiKey, ownerID, loadingOwnerID]);

  const handleNewIntegration = async () => {
    if (hasRun) return;
    if (!ownerID || apiKey === '') {
      return;
    }
    setHasRun(true);

    try {
      const linkData: IMentorkitLMSLinkRequest = {
        identifier: `Mentorkit Integration_${Date.now()}`,
        accessToken: apiKey,
        creatorOwnerId: ownerID,
        lmsTypeID: ELMSType.Mentorkit_LMS,
      };

      lmsIntegrationAgent.CourseLMSLink.AddLMSIntegration(linkData)
        .then((aResponse) => {
          if (aResponse.success === true) {
            setIntegrationStatus(EIntegrationStatus.completed);

            // Small delay to show the success message in modal, then redirect
            setTimeout(() => {
              history.push('/integrations?tab=Active%20Integrations');
            }, 2000);
          } else {
            setIntegrationStatus(EIntegrationStatus.error);
          }
          setHasRun(false);
        })
        .catch(() => {
          setIntegrationStatus(EIntegrationStatus.error);
        });
    } catch (error: any) {
      console.error('Error adding integration:', error);
      setIntegrationStatus(EIntegrationStatus.error);
    }
  };

  return (
    <>
      {!apiKey ? (
        <>
          <Typography variant='body2' color='textSecondary'>
            To create an integration with Mentorkit Enterprise LMS, log in to Mentorkit Admin, on the top right click your school name.
            <br />
            There will be a tab called &quot;Mentorkit&quot; that has a link that will create the connection automatically.
            <br />
            Or copy the API Key from the &quot;API&quot; tab and paste bellow.
          </Typography>
          <TextField label={'Mentorkit LMS API Key'} variant='outlined' value={apiKeyInput} onChange={handleApiKeyChange} placeholder={'API Key'} />

          <Button variant='contained' color='primary' component='a' style={{ marginTop: theme.spacing(1) }} onClick={() => handleSubmitApiKey()} disabled={!apiKeyInput.trim()}>
            {'Connect Mentorkit LMS'}
          </Button>
        </>
      ) : (
        <div style={{ marginTop: theme.spacing(3), padding: theme.spacing(2), background: theme.palette.background.paper, borderRadius: theme.spacing(1) }}>
          <Typography variant='h6' gutterBottom>
            Mentorkit Enterprise LMS Credentials Received:
          </Typography>
          <Typography variant='body1'>
            <strong>Api Key:</strong> {apiKey}
          </Typography>
          <Typography variant='body2' color='textSecondary' style={{ marginTop: theme.spacing(1) }}>
            Processing your integration...
          </Typography>
        </div>
      )}
      {/* Processing Modal */}
      <Modal
        open={integrationStatus !== EIntegrationStatus.notStarted}
        onClose={() => {
          // Only allow closing if status is error AND user clicked outside
          //console.log('integrationStatus', integrationStatus);
          if (integrationStatus === EIntegrationStatus.error) {
            setIntegrationStatus(EIntegrationStatus.notStarted);
            setApiKey('');
            setApiKeyParam(undefined);
          }
        }}
        closeAfterTransition
        slots={{ backdrop: Backdrop }}
        slotProps={{
          backdrop: {
            timeout: 500,
            sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' },
          },
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            bgcolor: theme.palette.background.paper,
            borderRadius: 2,
            boxShadow: 24,
            p: 4,
            outline: 'none',
            textAlign: 'center',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          {
            {
              [EIntegrationStatus.notStarted]: <>Integration has not started...</>,
              [EIntegrationStatus.pending]: (
                <>
                  <CircularProgress size={60} sx={{ mb: 3, color: theme.palette.primary.main }} />
                  <Typography variant='h5' gutterBottom sx={{ color: theme.palette.text.primary }}>
                    Setting up your integration
                  </Typography>
                  <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
                    Wrapping things up...
                  </Typography>
                </>
              ),
              [EIntegrationStatus.completed]: (
                <>
                  <CheckCircleIcon sx={{ fontSize: 60, color: theme.palette.success?.main || '#4caf50', mb: 3 }} />
                  <Typography variant='h5' gutterBottom sx={{ color: theme.palette.success?.main || '#4caf50' }}>
                    Integration Complete!
                  </Typography>
                  <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
                    Mentorkit LMS has been successfully connected. Redirecting you now...
                  </Typography>
                </>
              ),
              [EIntegrationStatus.error]: (
                <>
                  <CheckCircleIcon sx={{ fontSize: 60, color: theme.palette.error?.main, mb: 3 }} />
                  <Typography variant='h5' gutterBottom sx={{ color: theme.palette.error?.main }}>
                    An Error Occured!
                  </Typography>
                  <Typography variant='body1' sx={{ color: theme.palette.text.secondary }}>
                    Unable to create the connection. Try again or contact support.
                  </Typography>
                </>
              ),
            }[integrationStatus]
          }
        </Box>
      </Modal>
    </>
  );
}
